import { CodeType } from "@openapi";

/**
 * Default example programs for each playground type when user starts with a blank playground.
 * These examples demonstrate basic functionality and serve as a starting point for learning.
 */

export interface DefaultExample {
    code: string;
    input?: string; // Only for IO playgrounds (python-code, python-blocks)
    blocklyWorkspace?: string; // Only for block playgrounds (logo-blocks, python-blocks, python-turtle-blocks)
}

// =============================================================================
// Logo Code (Turtle Graphics) - logo-code
// =============================================================================
export const logoCodeExample: DefaultExample = {
    code: `; Draw a square spiral / Crtaj spiralu kvadrata
; Run the code to see the turtle draw! / Pokreni kod da vidiš kako se crta!
; Delete the code and try your own! / Obriši kod i pokušaj svoj!
; Template by Filip Udovčić / Predložak napravio Filip Udovčić

make "kut 91

to spiral :size
  if :size > 200 [stop]
  forward :size
  right :kut
  spiral :size + 5
end

ht
repeat 361 [
  cs pendown
  spiral 5
  make "kut :kut+1
  wait 1
]
seth 90
pu setxy xcor+90 ycor-130 pd
label "blockscode
`,
};

// =============================================================================
// Logo Blocks (Turtle Graphics) - logo-blocks
// =============================================================================
export const logoBlocksExample: DefaultExample = {
    code: `; This code is generated from blocks / Ovaj kod je generiran iz blokova
; Try dragging blocks from the toolbox! / Pokušaj prevući blokove iz alata!
; Example: Draw a square / Primjer: Nacrtaj kvadrat

repeat 4 [
  fd 100
  rt 90
]
`,
    blocklyWorkspace: `{"blocks":{"languageVersion":0,"blocks":[{"type":"main","id":"6yk-x;w\`$n:s]+6,R/L~","x":100,"y":100,"deletable":false,"inputs":{"STACK":{"block":{"type":"controls_repeat_ext","id":"1UtCH%,74kgrP0|tPh,|","inputs":{"TIMES":{"shadow":{"type":"math_number","id":"0F?z!R,N|T}DxzSV*yqC","fields":{"NUM":4}}},"DO":{"block":{"type":"turtle_fd","id":"o?Kyyeg1D2\`Rjf.m],PV","inputs":{"ARGUMENT":{"shadow":{"type":"math_number","id":"zA2vM%j2zW*_F%9e]?sH","fields":{"NUM":100}}}},"next":{"block":{"type":"turtle_rt","id":"f8}@NRoX,iu*c@9MU]Sn","inputs":{"ARGUMENT":{"shadow":{"type":"turtle_angle","id":"_/(\`*$e6v3/6hgz[hldq","fields":{"VALUE":90}}}}}}}}}}}}}]}}`,
};

// =============================================================================
// Python Code (I/O) - python-code
// =============================================================================
export const pythonCodeExample: DefaultExample = {
    code: `# Read a number and a list, then print the element at that index / Pročitaj broj i listu, zatim ispiši element na tom indeksu
# Click Run to execute! / Klikni Pokreni da izvršiš!
# Delete the code and try your own! / Obriši kod i pokušaj svoj!

index = int(input())
numbers = list(map(int, input().split()))
print(f"Element at index {index} is: {numbers[index]}")
`,
    input: `2
10 20 30 40 50`,
};

// =============================================================================
// Python Blocks (I/O) - python-blocks
// Note: blocklyWorkspace is not set - users start with empty workspace
// =============================================================================
export const pythonBlocksExample: DefaultExample = {
    code: `# This code is generated from blocks / Ovaj kod je generiran iz blokova
# Try dragging blocks from the toolbox! / Pokušaj prevući blokove iz alata!
# Example: Greet the user / Primjer: Pozdravi korisnika

name = input()
print("Hello, " + name + "!")
`,
    input: `World`,
};

// =============================================================================
// Python Turtle Code - python-turtle-code
// =============================================================================
export const pythonTurtleCodeExample: DefaultExample = {
    code: `# Draw a colorful star with Python turtle / Nacrtaj šarenu zvijezdu koristeći Python turtle
# Click Run to see the turtle draw! / Klikni Pokreni da vidiš kako se crta!

import turtle

colors = ["red", "orange", "yellow", "green", "blue", "purple"]

for i in range(6):
    turtle.color(colors[i])
    turtle.forward(100)
    turtle.right(144)
    turtle.forward(100)
    turtle.left(84)

turtle.done()
`,
};

// =============================================================================
// Python Turtle Blocks - python-turtle-blocks
// =============================================================================
export const pythonTurtleBlocksExample: DefaultExample = {
    code: `# This code is generated from blocks / Ovaj kod je generiran iz blokova
# Try dragging blocks from the toolbox! / Pokušaj prevući blokove iz alata!
# Example: Draw a square / Primjer: Nacrtaj kvadrat

import turtle

for i in range(4):
    turtle.forward(100)
    turtle.right(90)

turtle.done()
`,
    blocklyWorkspace: `{"blocks":{"languageVersion":0,"blocks":[{"type":"main","id":"F?YoAbVs}jReokhE|nCL","x":432,"y":138,"deletable":false,"inputs":{"STACK":{"block":{"type":"controls_repeat_ext","id":"7Cjk-3bijFkfuDE!DjN]","inputs":{"TIMES":{"shadow":{"type":"math_number","id":"S~ZB?=2wh./*TH#LS_,_","fields":{"NUM":4}}},"DO":{"block":{"type":"turtle_fd","id":"Q9,N#7[f?6s|GEp_u([r","inputs":{"ARGUMENT":{"shadow":{"type":"math_number","id":"x*=QJ,v.1w]#,NWkGYm$","fields":{"NUM":100}}}},"next":{"block":{"type":"turtle_rt","id":"]dF4cXJ9ue]ytEa_IX2}","inputs":{"ARGUMENT":{"shadow":{"type":"turtle_angle","id":"fCQa%+U@@UHd=fp-jB\`A","fields":{"VALUE":90}}}}}}}}}}}}}]}}`,
};

// =============================================================================
// C++ Code (I/O) - cpp-code
// =============================================================================
export const cppCodeExample: DefaultExample = {
    code: `// Read a number and a list, then print the element at that index / Pročitaj broj i listu, zatim ispiši element na tom indeksu
// Click Run to execute! / Klikni Pokreni da izvršiš!
// Delete the code and try your own! / Obriši kod i pokušaj svoj!

#include <iostream>
#include <vector>

using namespace std;

int main() {
    int count, index;
    cin >> count >> index;

    vector<int> numbers;
    for (int i = 0; i < count; i++) {
        int number;
        cin >> number;
        numbers.push_back(number);
    }

    cout << "Element at index " << index << " is: " << numbers.at(index) << endl;
    return 0;
}
`,
    input: `5 2
10 20 30 40 50`,
};

// =============================================================================
// Soldered MicroPython Code - soldered-code
// =============================================================================
export const solderedCodeExample: DefaultExample = {
    code: `from machine import Pin, I2C
from lcdI2C import LCD_I2C
import time

# Blink onboard LED (GPIO23, active LOW)
led = Pin(23, Pin.OUT)
for i in range(3):
    led.value(0)  # ON
    time.sleep_ms(300)
    led.value(1)  # OFF
    time.sleep_ms(300)

# Show message on LCD
i2c = I2C(0, sda=Pin(6), scl=Pin(7), freq=400000)
lcd = LCD_I2C(i2c, address=0x20)
lcd.begin()
lcd.backlight()
lcd.clear()
lcd.setCursor(0, 0)
lcd.print("Hello!")
lcd.setCursor(0, 1)
lcd.print("From Web BLE!")
print("Done!")
`,
};

// =============================================================================
// Soldered MicroPython Blocks - soldered-blocks
// =============================================================================
export const solderedBlocksExample: DefaultExample = {
    code: `from machine import Pin
import time

led = Pin(23, Pin.OUT)


for count in range(4):
  led.value(0)
  time.sleep_ms(500)
  led.value(1)
  time.sleep_ms(500)
print('Done!')
`,
    blocklyWorkspace: `{"blocks":{"languageVersion":0,"blocks":[{"type":"main","id":"soldered-main","x":100,"y":100,"deletable":false,"inputs":{"STACK":{"block":{"type":"controls_repeat_ext","id":"soldered-repeat","inputs":{"TIMES":{"shadow":{"type":"math_number","id":"soldered-num","fields":{"NUM":4}}},"DO":{"block":{"type":"nula_led","id":"soldered-led-on","fields":{"STATE":"ON"},"next":{"block":{"type":"nula_wait_ms","id":"soldered-wait-on","inputs":{"MS":{"shadow":{"type":"math_number","id":"soldered-wait-on-ms","fields":{"NUM":500}}}},"next":{"block":{"type":"nula_led","id":"soldered-led-off","fields":{"STATE":"OFF"},"next":{"block":{"type":"nula_wait_ms","id":"soldered-wait-off","inputs":{"MS":{"shadow":{"type":"math_number","id":"soldered-wait-off-ms","fields":{"NUM":500}}}}}}}}}}}}},"next":{"block":{"type":"text_print","id":"soldered-print","inputs":{"TEXT":{"shadow":{"type":"text","id":"soldered-print-text","fields":{"TEXT":"Done!"}}}}}}}}}}]}}`,
};

// =============================================================================
// Pybricks Code - pybricks-code
// =============================================================================
export const pybricksCodeExample: DefaultExample = {
    code: `from pybricks.hubs import PrimeHub
from pybricks.pupdevices import Motor, ColorSensor, UltrasonicSensor, ForceSensor
from pybricks.parameters import Button, Color, Direction, Port, Side, Stop
from pybricks.robotics import DriveBase
from pybricks.tools import wait, StopWatch

hub = PrimeHub()
left_motor = Motor(Port.A, Direction.COUNTERCLOCKWISE)
right_motor = Motor(Port.B)
robot = DriveBase(left_motor, right_motor, wheel_diameter=56, axle_track=82)

# Move forward 200mm
robot.straight(200)

# Turn right 90 degrees
robot.turn(90)
`,
};

// =============================================================================
// Pybricks Blocks - pybricks-blocks
// Note: blocklyWorkspace is not set - users start with empty workspace
// fixJsonForLoad will inject the main_pybricks block automatically
// =============================================================================
export const pybricksBlocksExample: DefaultExample = {
    code: `# This code is generated from blocks / Ovaj kod je generiran iz blokova
# Try dragging blocks from the toolbox! / Pokušaj prevući blokove iz alata!
# Connect to your Pybricks hub to run! / Spoji se na Pybricks hub za pokretanje!
`,
};

// =============================================================================
// Helper function to get default example by code type and playground mode
// =============================================================================
export type PlaygroundMode = "code" | "blocks";
export type PlaygroundType = "turtle" | "io";

export function getDefaultExample(
    codeType: CodeType,
    mode: PlaygroundMode,
    type: PlaygroundType,
): DefaultExample {
    if (codeType === CodeType.Logo) {
        return mode === "code" ? logoCodeExample : logoBlocksExample;
    }

    // Soldered
    if (codeType === CodeType.Soldered) {
        return mode === "code" ? solderedCodeExample : solderedBlocksExample;
    }

    // Pybricks
    if (codeType === CodeType.Pybricks) {
        return pybricksCodeExample;
    }
    if (codeType === CodeType.PybricksBlockly) {
        return pybricksBlocksExample;
    }

    // C++
    if (codeType === CodeType.Cpp) {
        return cppCodeExample;
    }

    // Python
    if (type === "turtle") {
        return mode === "code"
            ? pythonTurtleCodeExample
            : pythonTurtleBlocksExample;
    }

    // Python I/O
    return mode === "code" ? pythonCodeExample : pythonBlocksExample;
}

/**
 * Get default example by config key
 * Use this when you know the exact playground route
 */
export function getDefaultExampleByKey(
    key:
        | "logo-code"
        | "logo-blocks"
        | "python-code"
        | "python-blocks"
        | "python-turtle-code"
        | "python-turtle-blocks"
        | "cpp-code"
        | "pybricks-code"
        | "pybricks-blocks"
        | "soldered-code"
        | "soldered-blocks",
): DefaultExample {
    const examples: Record<string, DefaultExample> = {
        "logo-code": logoCodeExample,
        "logo-blocks": logoBlocksExample,
        "python-code": pythonCodeExample,
        "python-blocks": pythonBlocksExample,
        "python-turtle-code": pythonTurtleCodeExample,
        "python-turtle-blocks": pythonTurtleBlocksExample,
        "cpp-code": cppCodeExample,
        "pybricks-code": pybricksCodeExample,
        "pybricks-blocks": pybricksBlocksExample,
        "soldered-code": solderedCodeExample,
        "soldered-blocks": solderedBlocksExample,
    };
    return examples[key];
}

// Get default example by CodeType
export function getDefaultExampleForCodeType(
    codeType: CodeType,
): DefaultExample | null {
    switch (codeType) {
        case CodeType.Logo:
            return logoCodeExample;
        case CodeType.LogoBlockly:
            return logoBlocksExample;
        case CodeType.Python:
            return pythonCodeExample;
        case CodeType.PythonBlockly:
            return pythonBlocksExample;
        case CodeType.PythonTurtle:
            return pythonTurtleCodeExample;
        case CodeType.PythonTurtleBlockly:
            return pythonTurtleBlocksExample;
        case CodeType.Pybricks:
            return pybricksCodeExample;
        case CodeType.PybricksBlockly:
            return pybricksBlocksExample;
        case CodeType.Soldered:
            return solderedCodeExample;
        case CodeType.Cpp:
            return cppCodeExample;
        default:
            return null;
    }
}
